import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Loader2, Briefcase } from "lucide-react";
import { useWorkspace } from "../lib/workspaceContext";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

/**
 * EngagementCreateModal — used by Engagements page (Active tab) and the
 * Closed-Won handshake from Pipeline. Submits POST /v2/workspaces/{ws}/engagements.
 */
export default function EngagementCreateModal({
  open,
  onClose,
  workspaceId,
  defaultClientId = null,
  defaultClientName = "",
  defaultName = "",
  sourceDealId = null,
  onCreated,
}) {
  const { currency: ctxCurrency, region: ctxRegion } = useWorkspace();
  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [scopeMd, setScopeMd] = useState("");
  const [contractedValue, setContractedValue] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [region, setRegion] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sponsorName, setSponsorName] = useState("");
  const [sponsorContact, setSponsorContact] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName || "");
      setClientName(defaultClientName || "");
      setScopeMd("");
      setContractedValue("");
      setCurrency(ctxCurrency && ctxCurrency !== "All" ? ctxCurrency : "GBP");
      setRegion(ctxRegion && ctxRegion !== "All" ? ctxRegion : "");
      setStartDate("");
      setEndDate("");
      setSponsorName("");
      setSponsorContact("");
    }
  }, [open, defaultClientName, defaultName, ctxCurrency, ctxRegion]);

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Engagement name is required");
      return;
    }
    if (!workspaceId) {
      toast.error("Workspace not loaded yet");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: name.trim(),
        client_id: defaultClientId || null,
        client_name: clientName.trim() || null,
        scope_md: scopeMd.trim() || null,
        contracted_value: contractedValue ? parseFloat(contractedValue) : null,
        currency: currency || "GBP",
        region: region || null,
        start_date: startDate || null,
        end_date: endDate || null,
        sponsor_name: sponsorName.trim() || null,
        sponsor_contact: sponsorContact.trim() || null,
        source_deal_id: sourceDealId || null,
        status: "STARTING",
      };
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/engagements`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${r.status})`);
      }
      const created = await r.json();
      toast.success("Engagement created");
      onCreated?.(created);
      onClose?.();
    } catch (e) {
      toast.error(e.message || "Failed to create engagement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="engagement-create-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-blue-600" />
            {sourceDealId ? "Create engagement record now?" : "New engagement"}
          </DialogTitle>
          <DialogDescription>
            {sourceDealId
              ? "This deal just won. Capture scope and commercials so delivery can begin."
              : "Capture the scope, commercials, and sponsor for this engagement."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs text-slate-600">Engagement name *</Label>
            <Input
              data-testid="engagement-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Acme Senior Leader Programme — Cohort 1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Client</Label>
            <Input
              data-testid="engagement-client-input"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Client name"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Contracted value</Label>
            <Input
              data-testid="engagement-value-input"
              type="number"
              value={contractedValue}
              onChange={(e) => setContractedValue(e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Currency</Label>
            <select
              data-testid="engagement-currency-select"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm"
            >
              <option value="GBP">GBP</option>
              <option value="USD">USD</option>
              <option value="NGN">NGN</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Region</Label>
            <select
              data-testid="engagement-region-select"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm"
            >
              <option value="">—</option>
              <option value="UK">UK</option>
              <option value="NG">NG</option>
              <option value="RoA">RoA</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Start date</Label>
            <Input
              data-testid="engagement-start-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">End date</Label>
            <Input
              data-testid="engagement-end-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Sponsor name</Label>
            <Input
              data-testid="engagement-sponsor-name-input"
              value={sponsorName}
              onChange={(e) => setSponsorName(e.target.value)}
              placeholder="e.g., Jane Doe, CHRO"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Sponsor contact</Label>
            <Input
              data-testid="engagement-sponsor-contact-input"
              value={sponsorContact}
              onChange={(e) => setSponsorContact(e.target.value)}
              placeholder="email or phone"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-600">Scope (markdown ok)</Label>
            <Textarea
              data-testid="engagement-scope-input"
              rows={4}
              value={scopeMd}
              onChange={(e) => setScopeMd(e.target.value)}
              placeholder="Cohort size, modules, deliverables, success criteria..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="engagement-cancel-btn" disabled={submitting}>
            {sourceDealId ? "Skip for now" : "Cancel"}
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="engagement-submit-btn">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create engagement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
